// MF-568 — Card 'pat-response-matrix': difficulty-aware matrix. Same grid as the response chart, items grouped by
// skill area; the cell BACKGROUND is the item's challenge for that student (below / within / above score ± window,
// meta.card.options.itemWindow) and the symbol is the real response. "-" keeps the colour as a prediction only.
import { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE } from '../boardTheme'
import PatSkillsGrid, { Legend, Note } from './patSkillsGrid'
import { RESPONSE_STYLE, WINDOW_STYLE, adaptiveNote, buildMatrix, classifyItem } from './patSkillsShared'

const PatResponseMatrixCard = ({ data }) => {
  const matrix = useMemo(() => buildMatrix(data?.rows || [], { groupByStrand: true }), [data?.rows])
  const w = Number(data?.meta?.card?.options?.itemWindow)
  const hasWindow = Number.isFinite(w)
  const cellStyle = (s, it, response) => {
    const rel = hasWindow ? classifyItem(it.difficulty, s.score, w) : null
    const ws = rel ? WINDOW_STYLE[rel] : null
    return {
      bg: ws ? ws.bg : RESPONSE_STYLE['-'].bg,
      fg:
        response === 'X'
          ? RESPONSE_STYLE.X.fg
          : response === 'C'
            ? RESPONSE_STYLE.C.fg
            : SUMMARY_PALETTE.muted,
      opacity: response === '-' ? 0.6 : 1,
      title: `${RESPONSE_STYLE[response].label}${ws ? `; ${ws.label.toLowerCase()}` : ''}${response === '-' ? ' (prediction only)' : ''}`
    }
  }
  return (
    <Box sx={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: SUMMARY_PALETTE.ink, mb: 0.5 }}>
        Difficulty-aware response matrix{hasWindow ? ` (window ± ${w})` : ''}
      </Typography>
      <Legend
        entries={[
          { ...WINDOW_STYLE.below, symbol: '' },
          { ...WINDOW_STYLE.within, symbol: '' },
          { ...WINDOW_STYLE.above, symbol: '' },
          { symbol: 'C', bg: '#fff', fg: RESPONSE_STYLE.C.fg, label: 'Correct' },
          { symbol: 'X', bg: '#fff', fg: RESPONSE_STYLE.X.fg, label: 'Incorrect' },
          { symbol: '-', bg: '#fff', fg: SUMMARY_PALETTE.faint, label: 'Not administered' }
        ]}
      />
      {adaptiveNote(matrix) ? <Note>{adaptiveNote(matrix)}</Note> : null}
      <PatSkillsGrid matrix={matrix} cellStyle={cellStyle} bandByStrand />
      <Note>
        Read the combination: X on a below-window item suggests a possible gap to investigate. C on an
        above-window item suggests a possible strength to confirm. Colour on an unadministered item is a
        prediction only; it supplies no evidence of mastery. The window is the school&apos;s setting, not an
        ACER cut-off.
      </Note>
    </Box>
  )
}

export default PatResponseMatrixCard
